import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../firebase/config'; 
import { collection, doc } from 'firebase/firestore';

// Importamos la utilidad de persistencia centralizada y auditoría
import { saveToFirestore } from '../../firebase/cleanPayload'; 

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { cleanNumeric } from '../../utils/segurosUtils';
import { useBitacoraTimeline } from '../../utils/useBitacoraTimeline'; // ◄ Hook de Bitácoras Centralizado

// Listas Centralizadas
import { siniestroEstadosGestion, siniestroMappings, gestionTipos } from '../../data/listasDesplegables';

/**
 * FormularioSiniestro - Gestión y seguimiento de siniestros patrimoniales.
 * Refactorizado bajo arquitectura limpia e integrado con el Hook de Bitácoras Centralizado.
 */
const FormularioSiniestro = ({ 
    poliza, 
    clientData, 
    initialData = null, 
    onCancel, 
    onSuccess 
}) => {
    // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 2. ESTADOS LOCALES ---
    const [formData, setFormData] = useState({
        fechaCarga: new Date().toISOString().substring(0, 10),
        fechaSiniestro: '',
        tipoSiniestro: '',
        quienGestiono: '',
        numeroSiniestro: '',
        estadoGestion: 'Abierto',
        detalleSiniestro: '',
        cantidadRuedas: ''
    });

    const [newGestion, setNewGestion] = useState({ tipoGestion: '', observacion: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showRuedas, setShowRuedas] = useState(false);

    // --- 3. RESOLUCIÓN DE RUTAS TRANSACCIONALES ---
    const getResolvedPathData = () => {
        if (initialData?.path) {
            const parts = initialData.path.split('/');
            return { 
                cId: parts[3], 
                pId: parts[5], 
                sId: parts[7] 
            };
        }
        return {
            cId: clientData?.id || initialData?.clienteId,
            pId: poliza?.id || initialData?.polizaId,
            sId: initialData?.id
        };
    };

    const { cId, pId, sId } = getResolvedPathData();
    const riesgoPoliza = (poliza?.riesgo || initialData?.riesgo || '').toUpperCase().trim();
    const tipoSiniestroOptions = siniestroMappings[riesgoPoliza] || [];

    // Ruta de la subcolección de notas del siniestro (solo si ya existe el siniestro sId)
    const subcollectionPath = sId 
        ? `public/data/clientes/${cId}/polizas/${pId}/siniestros/${sId}/gestiones` 
        : null;

    // --- 4. INTEGRACIÓN DEL HOOK DE BITÁCORAS ---
    const {
        items: gestiones,            // Mapeamos a "gestiones" para mantener compatibilidad
        loading: loadingHistory,     // Mapeamos a "loadingHistory"
        editingId: editingGestionId, // Mapeamos a "editingGestionId"
        setEditingId: setEditingGestionId,
        fetchNotes,
        addLocalNote,
        updateLocalNote,
        removeLocalNote,
        persistBatchChanges
    } = useBitacoraTimeline('observacion');

    // --- 5. CARGA INICIAL E HIDRATACIÓN ---
    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
            if (cId && pId && sId) {
                // El hook se encarga de descargar y ordenar las notas en memoria
                fetchNotes(subcollectionPath);
            }
        } else {
            // ◄ MODIFICACIÓN: Al cargar un siniestro nuevo, forzamos que se mantenga vacío para mostrar el placeholder
            setFormData(prev => ({ ...prev, tipoSiniestro: '' }));
        }
    }, [initialData, cId, pId, sId, fetchNotes, subcollectionPath]);    

    // Visibilidad condicional para Robo de Ruedas en Automotores
    useEffect(() => {
        const esRoboRuedas = formData.tipoSiniestro === 'Robo de Ruedas';
        const esAutoMoto = ['AUTOMOTORES', 'MOTOVEHÍCULOS'].includes(riesgoPoliza);
        setShowRuedas(esRoboRuedas && esAutoMoto);
    }, [formData.tipoSiniestro, riesgoPoliza]);

    // --- 6. MEMORIZACIÓN Y ORDENAMIENTO DE LISTAS (useMemo) ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const sortedTiposSiniestro = useMemo(() => {
        return [...tipoSiniestroOptions].sort((a, b) => a.localeCompare(b));
    }, [tipoSiniestroOptions]);

    // ◄ MODIFICACIÓN: Preservamos el orden secuencial del proceso original sin ordenar alfabéticamente
    const memoizedGestionTipos = useMemo(() => {
        return gestionTipos || [];
    }, []);

    // --- 7. HANDLERS DE ACCIONES DE NOTAS DEL SINIESTRO ---
    const handleAddOrUpdateGestion = () => {
        if (!newGestion.tipoGestion || !newGestion.observacion) return;

        if (editingGestionId) {
            // Editamos de forma local en el hook
            updateLocalNote(editingGestionId, newGestion.tipoGestion, newGestion.observacion);
        } else {
            // Agregamos de forma local en el hook
            addLocalNote(newGestion.tipoGestion, newGestion.observacion);
        }
        setNewGestion({ tipoGestion: '', observacion: '' });
    };

    const handleEditClick = (g) => {
        setEditingGestionId(g.id || g.localId);
        setNewGestion({ tipoGestion: g.tipoGestion, observacion: g.observacion || '' });
    };

    const handleRemoveGestion = (id, isNew) => {
        // Si ya existe en Firebase, indicamos el path para el borrado físico inmediato.
        // Si es nuevo (isNew es true), se remueve únicamente del estado local de memoria.
        removeLocalNote(id, isNew ? null : subcollectionPath);
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleGestionChange = (e) => setNewGestion({ ...newGestion, [e.target.name]: e.target.value });

    // --- 8. PROCESAMIENTO Y GUARDADO FINAL ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {

            const payload = {
                ...formData,
                clienteId: cId,
                clienteNombre: (clientData?.nombre || formData.clienteNombre || initialData?.clienteNombre || '').toUpperCase().trim(),
                clienteApellido: (clientData?.apellido || formData.clienteApellido || initialData?.clienteApellido || '').toUpperCase().trim(),
                clienteCuit: clientData?.cuit || formData.clienteCuit || initialData?.clienteCuit || '',
                clienteDni: clientData?.dni || formData.clienteDni || initialData?.clienteDni || '',
                
                polizaId: pId,
                nPoliza: poliza?.nPoliza || formData.nPoliza || initialData?.nPoliza || '',
                compania: poliza?.compania || formData.compania || initialData?.compania || '',
                companiaId: poliza?.companiaId || formData.companiaId || initialData?.companiaId || '',

                riesgo: poliza?.riesgo || formData.riesgo || initialData?.riesgo || '',
                riesgoId: poliza?.riesgoId || formData.riesgoId || initialData?.riesgoId || '', 
                subriesgo: poliza?.subriesgo || formData.subriesgo || initialData?.subriesgo || '',
                
                canal: poliza?.canal || formData.canal || initialData?.canal || '',
                canalNombre: poliza?.canalNombre || poliza?.subcanal || formData.canalNombre || initialData?.canalNombre || '',
                canalNombreId: poliza?.canalNombreId || formData.canalNombreId || initialData?.canalNombreId || '',
                subcanalNombre: poliza?.subcanalNombre || formData.subcanalNombre || initialData?.subcanalNombre || '',
                subcanalNombreId: poliza?.subcanalNombreId || formData.subcanalNombreId || initialData?.subcanalNombreId || '',
            };

            const basePath = `public/data/clientes/${cId}/polizas/${pId}/siniestros`;
            const savedSiniestro = await saveToFirestore(basePath, payload, sId);

            // Persistimos las adiciones/modificaciones locales en lote (Batch) de forma unificada
            const finalSubcollectionPath = `public/data/clientes/${cId}/polizas/${pId}/siniestros/${savedSiniestro.id}/gestiones`;
            await persistBatchChanges(finalSubcollectionPath);

            if (onSuccess) {
                onSuccess(initialData ? 'Siniestro actualizado' : 'Siniestro registrado');
            }

        } catch (error) {
            console.error("Error crítico al guardar siniestro:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-8 space-y-6 animate-fade-in text-left font-sans transition-all duration-300">
            
            {/* 1. SECCIÓN: DATOS DEL SINIESTRO */}
            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                <legend className="px-3 text-lg font-bold text-slate-700 tracking-tight">Datos del Siniestro</legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha de Gestión</label>
                        <input type="date" name="fechaCarga" value={formData.fechaCarga} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-400 outline-none bg-slate-50/50 h-[46px] cursor-not-allowed select-none" readOnly />
                    </div>
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha del Siniestro *</label>
                        <input type="date" name="fechaSiniestro" value={formData.fechaSiniestro} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required />
                    </div>
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° de Siniestro (Cía)</label>
                        <input type="text" name="numeroSiniestro" value={formData.numeroSiniestro} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-red-600 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all uppercase" placeholder="N° de siniestro..." />
                    </div>
                </div>
            </fieldset>

            {/* 2. SECCIÓN: CLASIFICACIÓN Y ESTADO */}
            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
                <legend className="px-3 text-lg font-bold text-slate-700 tracking-tight">Clasificación y Estado</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tipo de Siniestro *</label>
                        <select name="tipoSiniestro" value={formData.tipoSiniestro} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required>
                            <option value="">Seleccione tipo...</option>
                            {sortedTiposSiniestro.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    
                    {showRuedas && (
                        <div className="animate-fade-in">
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Cantidad de Ruedas</label>
                            <input type="number" name="cantidadRuedas" value={formData.cantidadRuedas} onChange={handleChange} className="w-full border border-red-200 rounded-xl px-4 py-2.5 text-sm font-bold text-red-700 outline-none bg-red-50/30 h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" min="1" max="10" />
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Responsable *</label>
                        <select name="quienGestiono" value={formData.quienGestiono} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required>
                            <option value="">Seleccione...</option>
                            {sortedColaboradores.map(c => (
                                <option key={c.id} value={c.nombre}>{c.nombre}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estado de Gestión *</label>
                        <select name="estadoGestion" value={formData.estadoGestion} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-red-700 outline-none bg-red-50/20 h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required>
                            {siniestroEstadosGestion.map(e => (
                                <option key={e} value={e}>{e}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-6">
                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Detalle inicial del Siniestro</label>
                    <textarea name="detalleSiniestro" value={formData.detalleSiniestro} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none bg-white min-h-[100px] resize-none focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all placeholder:text-slate-300" placeholder="Descripción breve de los hechos y daños reclamados..." />
                </div>
            </fieldset>

            {/* 3. SECCIÓN: HISTORIAL DE GESTIONES */}
            <fieldset className="border border-red-200 rounded-xl p-6 relative bg-red-50/5 shadow-sm transition-all duration-300">
                <legend className="px-3 text-lg font-bold text-red-700 bg-white absolute -top-3.5 left-4 tracking-tight">Seguimiento de Trámite</legend>
                <div className={`p-5 border rounded-xl bg-white space-y-4 mb-6 shadow-sm transition-all duration-300 ${
                    editingGestionId ? 'border-orange-400 bg-orange-50/10' : 'border-red-100'
                }`}>
                    <div className="flex items-center justify-between border-b border-red-50 pb-2 mb-2 select-none">
                        <h4 className="text-xs font-black text-red-800 uppercase tracking-widest">{editingGestionId ? '✏️ Editando Nota' : 'Nueva Nota de Seguimiento'}</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* ◄ MODIFICACIÓN: Mapeamos directamente memoizedGestionTipos para mantener el orden secuencial */}
                        <select name="tipoGestion" value={newGestion.tipoGestion} onChange={handleGestionChange} className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none bg-white h-[46px] md:col-span-2 focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all">
                            <option value="">Tipo de Movimiento...</option>
                            {memoizedGestionTipos.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                        <button type="button" onClick={handleAddOrUpdateGestion} className={`font-bold rounded-xl text-xs uppercase tracking-widest px-4 h-[46px] text-white shadow-md active:scale-95 transition-all ${
                            editingGestionId ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-100' : 'bg-red-600 hover:bg-red-700 shadow-red-100'
                        }`}>
                            {editingGestionId ? 'Actualizar' : '+ Agregar'}
                        </button>
                    </div>
                    <textarea name="observacion" value={newGestion.observacion} onChange={handleGestionChange} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none bg-white min-h-[80px] resize-none focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all placeholder:text-slate-300" placeholder="Describa la gestión comercial, llamados o resoluciones..." />
                </div>

                {/* TABLA HISTÓRICA */}
                <div className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-inner scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    <table className="min-w-full text-xs text-left border-collapse">
                        <thead className="bg-slate-900 text-white font-bold uppercase text-[9px] tracking-widest sticky top-0 z-20 select-none">
                            <tr>
                                <th className="px-4 py-3 border-r border-slate-700">Fecha Registro</th>
                                <th className="px-4 py-3 border-r border-slate-700">Tipo</th>
                                <th className="px-4 py-3">Observación</th>
                                <th className="px-4 py-3 text-center w-20">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {gestiones.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-10 text-slate-400 italic">No hay notas registradas.</td>
                                </tr>
                            ) : (
                                gestiones.map((g) => (
                                    <tr key={g.id || g.localId} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-4 py-3 font-bold text-slate-500 tabular-nums">
                                            {g.fechaCreacion?.seconds 
                                                ? new Date(g.fechaCreacion.seconds * 1000).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) 
                                                : (g.fechaCreacion ? new Date(g.fechaCreacion).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : 'Pendiente...')}
                                        </td>
                                        <td className="px-4 py-3 font-black text-blue-700 uppercase">{g.tipoGestion}</td>
                                        <td className="px-4 py-3 text-slate-600 leading-relaxed italic">{g.observacion}</td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button type="button" onClick={() => handleEditClick(g)} className="text-slate-300 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-all">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z" />
                                                    </svg>
                                                </button>
                                                <button type="button" onClick={() => handleRemoveGestion(g.id || g.localId, g.isNew)} className="text-slate-300 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </fieldset>

            {/* SECCIÓN INFERIOR: BOTONERA DE ACCIONES */}
            <div className="pt-6 border-t border-slate-100 flex justify-end gap-3 bg-white relative z-10">
                <button 
                    type="button" 
                    onClick={onCancel} 
                    className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs shadow-sm hover:bg-slate-50 transition-all transform active:scale-95 select-none"
                >
                    Cancelar
                </button>
                <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="px-8 py-2.5 rounded-full bg-red-600 text-white font-bold shadow-lg shadow-red-100 disabled:opacity-50 uppercase text-xs hover:bg-red-700 transition-all transform active:scale-95 select-none min-w-[160px]"
                >
                    {isSubmitting ? 'Guardando...' : initialData ? 'Guardar Cambios' : 'Confirmar Siniestro'}
                </button>
            </div>
        </form>
    );
};

export default FormularioSiniestro;